/**
 * Selection insights for eligible template cards.
 * Uses embedded eligible `metrics` (ATT-28). Placeholder fallback only if metrics are absent (older BE).
 */
import type IEligibleAgentTemplate from 'store/slices/valueChainMap/interfaces/IEligibleAgentTemplate';

export type TAgentTemplateConfirmedOutputKind =
  | 'materials'
  | 'suppliers'
  | 'findings'
  | 'signals'
  | 'regulations'
  | 'exposures'
  | 'entities';

export type TAgentTemplateSelectionInsights = {
  runCount: number,
  /** null when API reports no runs (runCount === 0). */
  successRatePercent: number | null,
  confirmedOutputCount: number,
  confirmedOutputKind: TAgentTemplateConfirmedOutputKind,
  isPlaceholder: boolean,
};

const CONFIRMED_KIND_BY_UI_TYPE: Record<string, TAgentTemplateConfirmedOutputKind> = {
  bom: 'materials',
  'supplier-investigation': 'suppliers',
  'reputation-monitoring': 'signals',
  'regulation-monitoring': 'regulations',
  'finance-monitoring': 'findings',
  'climate-monitoring': 'exposures',
  'ownership-monitoring': 'entities',
};

function resolveConfirmedOutputKind(uiAgentType?: string): TAgentTemplateConfirmedOutputKind {
  if (!uiAgentType) {
    return 'findings';
  }
  return CONFIRMED_KIND_BY_UI_TYPE[uiAgentType] || 'findings';
}

function buildPlaceholderInsights(
  template: Pick<IEligibleAgentTemplate, 'id' | 'promptVersion' | 'uiAgentType'>,
): TAgentTemplateSelectionInsights {
  const seed = template.id * 17 + template.promptVersion * 3;

  return {
    runCount: 12 + (seed % 187),
    successRatePercent: 72 + (seed % 27),
    confirmedOutputCount: 8 + (seed % 143),
    confirmedOutputKind: resolveConfirmedOutputKind(template.uiAgentType),
    isPlaceholder: true,
  };
}

function buildAgentTemplateSelectionInsights(
  template: IEligibleAgentTemplate,
): TAgentTemplateSelectionInsights {
  const { metrics } = template;
  if (!metrics) {
    return buildPlaceholderInsights(template);
  }

  return {
    runCount: metrics.runCount,
    successRatePercent: metrics.successRatePercent,
    confirmedOutputCount: metrics.confirmedOutputCount,
    confirmedOutputKind: metrics.confirmedOutputKind || resolveConfirmedOutputKind(template.uiAgentType),
    isPlaceholder: false,
  };
}

export default buildAgentTemplateSelectionInsights;
